export enum CanonicalTaskGroup {
  INTAKE = 'Intake',
  INITIAL_REVIEW = 'Initial Review',
  POLICE_REPORT_INVESTIGATION = 'Police Report & Investigation',
  FIRST_PARTY_INSURANCE = '1P Insurance',
  THIRD_PARTY_INSURANCE = '3P Insurance',
  MEDICAL_RECORDS = 'Medical Records',
  TREATMENT = 'Treatment',
  DEMAND_LETTER = 'Demand Letter',
  LIENS = 'Liens',
  MISCELLANEOUS = 'Miscellaneous',
  ADDITIONAL_INSURANCE = 'Additional Insurance',
}

/**
 * Display name overrides for task groups.
 * Used to show user-friendly names in the UI while keeping data values stable.
 */
export const TASK_GROUP_DISPLAY_NAMES: Partial<
  Record<CanonicalTaskGroup, string>
> = {
  // No overrides needed - canonical names now match display names
};

/**
 * Get the display name for a task group.
 * Returns the override if one exists, otherwise returns the original value.
 */
export const getTaskGroupDisplayName = (
  group: CanonicalTaskGroup | string,
): string => {
  return TASK_GROUP_DISPLAY_NAMES[group as CanonicalTaskGroup] ?? String(group);
};

export enum CanonicalTaskType {
  MEDICAL_RECORDS = 'Medical Records',
  MEDICAL_BILLS = 'Medical Bills',
  LOR_ACKNOWLEDGEMENT = 'LOR Acknowledgement',
  SEND_LOR = 'Send LOR',
  OPEN_CLAIM = 'Open Claim',
  GET_LIABILITY_LIMITS = 'Get Liability Limits',
  FIND_1P_COVERAGE = 'Find 1P Coverage',
  FIND_3P_COVERAGE = 'Find 3P Coverage',
  FINAL_LIEN = 'Final Lien',
  POLICE_REPORT = 'Police Report',
  OPEN_FILE = 'Open File',
  LOCATE_REPORT_SOURCE = 'Locate Report Source',
  ASCERTAIN_SUBROGATOR = 'Ascertain Subrogator',
  FINALIZE_DEMAND = 'Finalize Demand',
  DRAFT_DEMAND = 'Draft Demand',
  DECLARATION_PAGE = 'Declaration Page',
  HEALTH_INSURANCE_CARD = 'Health Insurance Card',
  TREATMENT_CHECK_IN = 'Treatment Check-In',
  TREATMENT_BARRIER = 'Treatment Barrier',
  HIPAA_DIRECTIVE = 'HIPAA Directive',
  RETAINER_AGREEMENT = 'Retainer Agreement',
  REVIEW_FOUND_COVERAGE = 'Review Found Coverage',
  REVIEW_FOUND_PROVIDER = 'Review Found Provider',
  CONDUCT_INTAKE = 'Conduct Intake',
  CONDUCT_INITIAL_REVIEW = 'Conduct Initial Review',
  // Premises Liability task types
  REVIEW_INCIDENT_PROPERTIES = 'Review Incident Properties',
  CONFIRM_INCIDENT_LOCATION = 'Confirm Incident Location',
  RESEARCH_PROPERTY_OWNERS = 'Research Property & Owners',
  // Note: Premises LOR uses the regular SEND_LOR task type
  OPEN_PREMISES_CLAIM = 'Open Premises Claim',
  RESEARCH_ALTERNATE_CONTACTS = 'Research & Identify Alternate Contacts',
  OTHER = 'Other',
  ALL = 'All',
}

/**
 * Backend TaskTemplateType values used for branching on task template variants.
 * Matches values from app/defs/tasks.py TaskTemplateType enum.
 */
export const TASK_TEMPLATE_TYPES = {
  COVERAGE_VERIFY_1P_COVERAGE: 'coverage_verify_1p_coverage',
  TREATMENT_RECORD_REQUEST: 'treatment_record_request',
  CLIENT_PORTAL_REVIEW_UPLOADS: 'client_portal_review_uploads',
} as const;

export enum CanonicalTaskStatus {
  PENDING = 'Pending',
  BLOCKED = 'Blocked',
  IN_PROGRESS = 'In Progress',
  COMPLETED = 'Completed',
  NOT_APPLICABLE = 'Not Applicable',
  CLOSED = 'Closed',
}

/**
 * Priority labels for task filtering.
 * Values match backend TaskPriorityLabel enum (lowercase).
 *
 * Priority is independent of due date - use due_date_range filter for overdue tasks.
 */
export enum TaskPriorityLabel {
  IMMEDIATE = 'immediate',
  URGENT = 'urgent',
  NORMAL = 'normal',
}

export enum TaskExternalCMSUpdateStatus {
  NOT_UPDATED = 'NOT_UPDATED',
  UPDATED = 'UPDATED',
}

export enum SLAClockStartEvent {
  TASK_MADE_ACTIVE = 'TASK_MADE_ACTIVE',
  ACTIVE_TASK_CREATED = 'ACTIVE_TASK_CREATED',
}

export enum SLAPolicy {
  SAME_DAY = '0_DAYS',
  ONE_DAY = '1_DAY',
  TWO_DAYS = '2_DAYS',
  THREE_DAYS = '3_DAYS',
  SEVEN_DAYS = '7_DAYS',
  FOURTEEN_DAYS = '14_DAYS',
  THIRTY_DAYS = '30_DAYS',
  FORTY_FIVE_DAYS = '45_DAYS',
}

// Shared constant – statuses considered "active work" across UI and backend counts
export const ACTIVE_WORK_STATUSES: CanonicalTaskStatus[] = [
  CanonicalTaskStatus.PENDING,
  CanonicalTaskStatus.IN_PROGRESS,
  CanonicalTaskStatus.BLOCKED,
];

export const TaskStatusColors = {
  [CanonicalTaskStatus.PENDING]: 'bg-gray-100 text-gray-700',
  [CanonicalTaskStatus.BLOCKED]: 'bg-red-100 text-red-700',
  [CanonicalTaskStatus.IN_PROGRESS]: 'bg-blue-100 text-blue-700',
  [CanonicalTaskStatus.COMPLETED]: 'bg-green-100 text-green-700',
  [CanonicalTaskStatus.NOT_APPLICABLE]: 'bg-gray-100 text-gray-700',
  [CanonicalTaskStatus.CLOSED]: 'bg-gray-200 text-gray-800',
};

export const TASKS_WITH_ATTACHED_MEDICAL_PROVIDER = [
  CanonicalTaskType.MEDICAL_RECORDS,
  CanonicalTaskType.POLICE_REPORT,
];

export const TASKS_WITH_ATTACHED_INSURANCE_POLICY = [
  CanonicalTaskType.LOR_ACKNOWLEDGEMENT,
  CanonicalTaskType.SEND_LOR,
  CanonicalTaskType.OPEN_CLAIM,
  CanonicalTaskType.DECLARATION_PAGE,
];

export const TASKS_WITH_VOICE_AI_ACTION = [
  ...TASKS_WITH_ATTACHED_INSURANCE_POLICY,
  ...TASKS_WITH_ATTACHED_MEDICAL_PROVIDER,
];

export const TASKS_WITH_CLIENT_CALL_ACTION = [
  CanonicalTaskType.TREATMENT_CHECK_IN,
  CanonicalTaskType.HIPAA_DIRECTIVE,
  CanonicalTaskType.RETAINER_AGREEMENT,
  CanonicalTaskType.CONDUCT_INTAKE,
  CanonicalTaskType.DRAFT_DEMAND,
  CanonicalTaskType.FINALIZE_DEMAND,
];

export interface Contact {
  id: string;
  name: string;
  phone: string;
  type: ContactType;
  secondaryTitle?: string;
  // For contacts that are handles/subcontacts, store the parent entity ID
  parentId?: string;
}

export enum ContactType {
  MEDICAL_PROVIDER = 'Medical Provider',
  INSURANCE_CARRIER = 'Insurance Carrier',
  LAW_ENFORCEMENT_AGENCY = 'Law Enforcement Agency',
  INSURANCE_ADJUSTER = 'Insurance Adjuster',
  OTHER = 'Other',
}

export const INSURANCE_CARRIER_CONTACTS: Contact[] = [
  {
    id: 'state-farm',
    name: 'State Farm',
    phone: '(800) 732-5246',
    type: ContactType.INSURANCE_CARRIER,
  },
  {
    id: 'progressive',
    name: 'Progressive',
    phone: '(800) 776-4737',
    type: ContactType.INSURANCE_CARRIER,
  },
  {
    id: 'cure',
    name: 'Cure',
    phone: '(800) 535-2873',
    type: ContactType.INSURANCE_CARRIER,
  },
  {
    id: 'usa-underwriters',
    name: 'USA Underwriters',
    phone: '(855) 230-1656',
    type: ContactType.INSURANCE_CARRIER,
  },
  {
    id: 'allstate',
    name: 'Allstate',
    phone: '(800) 255-7828',
    type: ContactType.INSURANCE_CARRIER,
  },
  {
    id: 'geico',
    name: 'Geico',
    phone: '(800) 841-3000',
    type: ContactType.INSURANCE_CARRIER,
  },
  {
    id: 'plymouth-rock',
    name: 'Plymouth Rock',
    phone: '(844) 346-1225',
    type: ContactType.INSURANCE_CARRIER,
  },
  {
    id: 'bristol-west',
    name: 'Bristol West',
    phone: '(800) 274-7865',
    type: ContactType.INSURANCE_CARRIER,
  },
  {
    id: 'farmers',
    name: 'Farmers Insurance',
    phone: '(800) 435-7764',
    type: ContactType.INSURANCE_CARRIER,
  },
];
